import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAssets from '@/src/Processing/components/hooks/useAssets';
import { Dialog, TextInput } from './forms';
import './asset-picker.css';

interface Asset {
    id: number;
    name?: string;
    url: string;
    createdAt?: string;
}

/** Same calls as the v2 AssetPicker: list org assets, upload to "customizer", or paste a URL. */
export default function AssetPicker({
    onSelect,
    onClose,
    title = 'Choose an image',
}: {
    onSelect: (url: string) => void;
    onClose: () => void;
    title?: string;
}) {
    const api = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const { uploadAsset } = useAssets();
    const [assets, setAssets] = useState<Asset[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [search, setSearch] = useState('');
    const [url, setUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let mounted = true;
        api.get(`/${orgId}/assets`)
            .then((r: any) => {
                if (!mounted) return;
                const list: Asset[] = Array.isArray(r.data) ? r.data : [];
                setAssets(
                    [...list].sort(
                        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
                    ),
                );
            })
            .catch(() => mounted && setFailed(true));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const upload = async (file: File) => {
        if (uploading) return;
        setUploading(true);
        setUploadError('');
        try {
            const result = await uploadAsset({
                // @ts-ignore — FormData accepts File
                asset: file,
                name: file.name,
                folder: 'customizer',
                orgId: String(orgId),
            });
            if (result?.url) onSelect(result.url);
            else setUploadError('Upload didn’t return an image — try again.');
        } catch {
            setUploadError('Couldn’t upload that file — try again.');
        } finally {
            setUploading(false);
        }
    };

    const q = search.trim().toLowerCase();
    const shown = (assets ?? []).filter((a) => (a.name ?? '').toLowerCase().includes(q));

    return (
        <div className="asset-picker">
            <Dialog
                open
                title={title}
                onClose={onClose}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="tb-new"
                            style={{ marginLeft: 0 }}
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                        >
                            {uploading ? 'Uploading…' : 'Upload image'}
                        </button>
                    </>
                }
            >
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.cube"
                    hidden
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(f);
                        e.target.value = '';
                    }}
                />
                <div className="asset-picker-bar">
                    <TextInput
                        aria-label="Search assets"
                        placeholder="Search assets"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <form
                    className="asset-picker-bar"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (url.trim()) onSelect(url.trim());
                    }}
                >
                    <TextInput
                        aria-label="Image URL"
                        className="input ap-mono"
                        placeholder="…or paste an image URL"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                    />
                    <button type="submit" className="tb-btn" disabled={!url.trim()}>
                        Use URL
                    </button>
                </form>
                {uploadError && <p className="field-e" role="alert">{uploadError}</p>}
                {failed ? (
                    <p className="quiet-empty">Couldn’t load your assets. You can still upload or paste a URL.</p>
                ) : assets === null ? (
                    <div className="ap-grid" aria-busy="true" aria-label="Loading assets">
                        {Array.from({ length: 10 }, (_, i) => (
                            <span className="ap-thumb sk" key={i}>
                                <span className="im" />
                            </span>
                        ))}
                    </div>
                ) : shown.length === 0 ? (
                    <p className="quiet-empty">{q ? 'No assets match.' : 'No assets yet — upload one.'}</p>
                ) : (
                    <div className="ap-grid">
                        {shown.map((a) => (
                            <button type="button" key={a.id} className="ap-thumb" title={a.name} onClick={() => onSelect(a.url)}>
                                <span className="im">
                                    <img src={a.url} alt={a.name ?? ''} loading="lazy" />
                                </span>
                                <span className="nm">{a.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </Dialog>
        </div>
    );
}
